import type { MjcfJoint, Rig } from '@/domain/rig'
import type { PlaneKey, Side, Simulation } from '@/domain/hs1'
import { modelId } from './fingerprint'

/**
 * Die Referenzbewegung als Datensatz — was die Trainingsstrecke braucht.
 *
 * WOFÜR. Ein Laufregler wird nicht aus dem Nichts gelernt; er lernt gegen ein
 * VORBILD. Das Vorbild ist der Gang, den dieser Kern rechnet — und damit die
 * Trainingsstrecke ihn benutzen kann, muss er in einer Form vorliegen, die dort
 * lesbar ist: eine Zahlenreihe je Achse, in der Reihenfolge der Achsen des
 * Modells, mit Zeit und Einheit daneben.
 *
 * DIE REIHENFOLGE IST DIE DES MJCF UND KEINE ANDERE. Isaac Lab und Newton
 * bekommen dieselbe Datei, aus der auch der Prüfstand rechnet; die Achsen darin
 * stehen in der Reihenfolge, in der MuJoCo sie führt. Eine zweite Reihenfolge
 * wäre nicht falsch SICHTBAR — sie drehte nur die falschen Gelenke, und das
 * fällt erst nach dem ersten Training auf.
 *
 * BOGENMASS, NICHT GRAD. Der Kern führt Grad, weil Menschen darin lesen. Jede
 * Robotikumgebung führt Bogenmass. Die Umrechnung passiert HIER, einmal, und
 * `MotionExport.unit` sagt es ausdrücklich — die dritte Stelle im Projekt, an
 * der diese Falle gestellt wird.
 *
 * WAS NICHT DRINSTEHT: Momente. Die Trainingsstrecke soll den Regler finden,
 * der die Bewegung erzeugt, und nicht die Momente nachfahren, die unsere
 * Rückwärtsrechnung dafür braucht. Wer sie mitgäbe, hätte die Antwort in die
 * Aufgabe geschrieben. Die Momentgrenzen stehen ohnehin im MJCF, wo sie
 * hingehören.
 */

/** Welche Ebene des Gangmodells zu welcher Drehachse gehört. */
const PLANE_OF_AXIS: Record<string, PlaneKey> = { y: 'sag', x: 'fro', z: 'tra' }

const DEG = Math.PI / 180

export interface MotionJoint {
  /** Name im MJCF — der Schlüssel, unter dem die Umgebung die Achse findet. */
  name: string
  node: string
  axis: string
  side: string
  /** Gelenkgrenzen, RAD. */
  lower: number
  upper: number
  /** Momentgrenze aus der Auslegung, Nm. */
  effort: number
  /**
   * Passiv — die Achse hat KEINEN Antrieb.
   *
   * DAS ZEHENGRUNDGELENK IST EINE FEDER. Es trägt ein Moment und lässt sich
   * trotzdem nicht kommandieren; ein Regler, der es in seinen Aktionsraum
   * nimmt, lernt gegen ein Stellglied, das es nicht gibt. Ohne dieses Feld
   * sieht die Feder in der Datei aus wie jede andere Achse mit 110 Nm.
   */
  passive: boolean
  /**
   * Führt der Referenzgang diese Achse?
   *
   * `false` heisst: die Reihe ist null, und das ist eine VORGABE und keine
   * Bewegung. Wer sie als Vorbild nimmt, trainiert einen Regler darauf, die
   * Achse stillzuhalten.
   */
  recorded: boolean
}

export interface MotionExport {
  /** Name des Modells — derselbe wie im MJCF. */
  model: string
  /**
   * Die Modellkennung — WELCHER Stand.
   *
   * Sie steht in jeder erzeugten Datei dieser Kette. Ein Trainingslauf, der
   * sie im Datensatz führt, gehört zu genau einem Modell; zwei Dateien aus
   * verschiedenen Ständen unterscheiden sich auf den ersten Blick. Siehe
   * `fingerprint.ts`.
   */
  modelId: string
  /** Welche Gangart. */
  gait: string
  /** Einheit der Winkel. Steht ausdrücklich da, weil sie die häufigste Falle ist. */
  unit: 'rad'
  /** Dauer des Doppelschritts, s. */
  cycleTime: number
  /** Stützstellen je Zyklus. */
  samples: number
  /** Zeitschritt zwischen zwei Stützstellen, s. */
  dt: number
  /** Fortbewegungsgeschwindigkeit, m/s — das Ziel, gegen das belohnt wird. */
  speed: number
  /** Gesamtmasse, kg. Zur Gegenprobe gegen das MJCF. */
  mass: number
  joints: MotionJoint[]
  /**
   * Die Winkel: eine Zeile je Stützstelle, eine Spalte je Achse.
   *
   * ZEILENWEISE UND NICHT SPALTENWEISE. So liest jede Umgebung einen
   * Bewegungsdatensatz: ein Zeitschritt ist ein Zustand. Spaltenweise wäre
   * kompakter zu schreiben und müsste bei jedem Lesen umsortiert werden.
   */
  qpos: number[][]
  /**
   * Die Winkelgeschwindigkeiten, rad/s.
   *
   * ZYKLISCH ABGELEITET: der Gang schliesst sich, die letzte Stützstelle geht
   * in die erste über. Wer am Rand einseitig ableitet, bekommt dort einen
   * Ausschlag, den die Bewegung nicht hat — und der Regler lernt, ihn
   * nachzufahren.
   */
  qvel: number[][]
  /** Höhe des Beckens über dem Boden je Stützstelle, m. */
  rootHeight: number[]
  /**
   * Woher die Zahlen kommen.
   *
   * Ein Datensatz ohne Herkunft ist in einem halben Jahr eine Datei, von der
   * niemand weiss, welcher Modellstand darin steckt.
   */
  provenance: string
}

/**
 * Baut den Bewegungsdatensatz aus einer gerechneten Simulation.
 *
 * Rein: gleiche Simulation, gleicher Datensatz. Damit lässt er sich prüfen wie
 * jede andere Rechnung — und zwei Läufe desselben Standes ergeben Zeichen für
 * Zeichen dieselbe Datei.
 */
export function motionExport(
  simulation: Simulation,
  rig: Rig,
  joints: readonly MjcfJoint[],
  options: { model?: string; provenance?: string } = {},
): MotionExport {
  const kennung = modelId(simulation.model, options.model ?? 'gen5')
  const samples = simulation.gait.samples
  const cycleTime = simulation.gait.cycleTime
  const dt = samples > 0 ? cycleTime / samples : 0
  const angles = simulation.gait.angles

  const spalten: { joint: MotionJoint; werte: Float32Array }[] = joints.map((joint) => {
    const role = rig.byId[joint.node]?.role ?? null
    const plane = PLANE_OF_AXIS[joint.axis]
    const side: Side = joint.side === 'L' ? 'L' : 'R'
    const reihe = role && plane ? angles[role]?.[plane]?.[side] : undefined

    const werte = new Float32Array(samples)
    if (reihe) for (let i = 0; i < samples; i++) werte[i] = (reihe[i] ?? 0) * DEG

    return {
      joint: {
        name: joint.name,
        node: joint.node,
        axis: joint.axis,
        side: joint.side,
        lower: joint.lo * DEG,
        upper: joint.hi * DEG,
        effort: joint.moment,
        passive: joint.spring,
        recorded: reihe !== undefined,
      },
      werte,
    }
  })

  const qpos: number[][] = []
  for (let i = 0; i < samples; i++) qpos.push(spalten.map((s) => s.werte[i] ?? 0))

  /* Zyklisch mittig abgeleitet — siehe `qvel`. */
  const qvel: number[][] = []
  for (let i = 0; i < samples; i++) {
    const vor = (i - 1 + samples) % samples
    const nach = (i + 1) % samples
    qvel.push(
      spalten.map((s) =>
        dt > 0 ? ((s.werte[nach] ?? 0) - (s.werte[vor] ?? 0)) / (2 * dt) : 0,
      ),
    )
  }

  const rootHeight: number[] = []
  for (let i = 0; i < samples; i++) {
    rootHeight.push(simulation.dynamics.poses[i]?.pelvis.position.y ?? 0)
  }

  return {
    model: options.model ?? 'gen5',
    modelId: kennung,
    gait: simulation.state.gait,
    unit: 'rad',
    cycleTime,
    samples,
    dt,
    speed: simulation.metrics.speed,
    mass: simulation.metrics.mass,
    joints: spalten.map((s) => s.joint),
    qpos,
    qvel,
    rootHeight,
    provenance:
      options.provenance ??
      `NEURA Performance · Modellstand ${kennung} · erzeugt aus dem Punktsatz, nicht von Hand gepflegt`,
  }
}

/**
 * Der Datensatz als JSON-Text.
 *
 * MIT UMBRÜCHEN JE ZEITSCHRITT und nicht als eine Zeile: die Datei wird von
 * Menschen gelesen, wenn ein Training nicht tut, was es soll, und eine Zeile
 * mit zweihunderttausend Zahlen ist dann nicht lesbar. Der Aufpreis ist ein
 * Zeilenumbruch je Zeitschritt.
 */
export function motionJson(motion: MotionExport): string {
  const zeilen = (rows: number[][]): string =>
    rows.map((r) => `    [${r.map((v) => v.toFixed(6)).join(', ')}]`).join(',\n')

  return [
    '{',
    `  "model": ${JSON.stringify(motion.model)},`,
    `  "modelId": ${JSON.stringify(motion.modelId)},`,
    `  "gait": ${JSON.stringify(motion.gait)},`,
    `  "unit": ${JSON.stringify(motion.unit)},`,
    `  "cycleTime": ${motion.cycleTime.toFixed(6)},`,
    `  "samples": ${motion.samples},`,
    `  "dt": ${motion.dt.toFixed(6)},`,
    `  "speed": ${motion.speed.toFixed(6)},`,
    `  "mass": ${motion.mass.toFixed(6)},`,
    `  "provenance": ${JSON.stringify(motion.provenance)},`,
    `  "joints": ${JSON.stringify(motion.joints)},`,
    '  "qpos": [',
    zeilen(motion.qpos),
    '  ],',
    '  "qvel": [',
    zeilen(motion.qvel),
    '  ],',
    `  "rootHeight": [${motion.rootHeight.map((v) => v.toFixed(6)).join(', ')}]`,
    '}',
    '',
  ].join('\n')
}
